import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Wrench, Box, MapPin, FileText, ShoppingBag, Plus, Loader2, RefreshCw,
  Warehouse, PackageCheck, Activity, Trash2, Search, ChevronRight, Radio,
  Grid3x3, Route as RouteIcon, Scale, QrCode, Tv2, PlayCircle
} from 'lucide-react';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import { CatalogPicker } from '../components/vending/CatalogPicker';
import RichVendingClientDialog from '../components/vending/RichVendingClientDialog';
// v2.5.897 — VendingOnboardingTour is now mounted at AppLayout level for all
// /vending/* routes. Do not re-mount here (would duplicate the overlay).

const API = `${process.env.REACT_APP_BACKEND_URL}/api/vending`;

const STATUS_STYLES = {
  deployed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  warehouse: 'bg-gray-100 text-gray-700 border-gray-200',
  servicing: 'bg-amber-100 text-amber-800 border-amber-200',
  in_transit: 'bg-sky-100 text-sky-800 border-sky-200',
  decommissioned: 'bg-rose-100 text-rose-800 border-rose-200',
};

const TYPE_LABELS = {
  snack: 'Snack', cold_drink: 'Cold Drink', combo: 'Combo',
  hot_beverage: 'Hot Beverage', hot_food: 'Hot Food',
  ppe: 'PPE', pharmacy: 'Pharmacy', other: 'Other',
};

export default function VendingFleetPage() {
  const routerLocation = useLocation();
  const navigate = useNavigate();
  // Derive active tab from URL segment: /vending/machines → machines, /vending → machines (default)
  const pathSegment = routerLocation.pathname.replace(/^\/vending\/?/, '').split('/')[0];
  const validTabs = ['machines', 'locations', 'clients', 'products'];
  const activeTab = validTabs.includes(pathSegment) ? pathSegment : 'machines';
  const handleTabChange = (tab) => {
    navigate(tab === 'machines' ? '/vending' : `/vending/${tab}`);
  };

  const [dashboard, setDashboard] = useState(null);
  const [machines, setMachines] = useState([]);
  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', type: '', search: '' });
  const [dialog, setDialog] = useState(null); // 'machine'|'client'|'location'|'product'|null
  const [editing, setEditing] = useState(null);
  const [locked, setLocked] = useState(false);      // 402 payment required
  const [demo, setDemo] = useState(null);           // {has_demo, total_demo, total_live}
  const [clearingDemo, setClearingDemo] = useState(false);
  const [reseedingEnterprise, setReseedingEnterprise] = useState(false);
  const [seedingDemo, setSeedingDemo] = useState(false);
  // v2.5.883 — Companion entry-point visibility (Platform Owner / Enterprise can hide either).
  const [companionEntry, setCompanionEntry] = useState({ visible: true, has_access: true });
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/vending/companion/settings`)
      .then(r => setCompanionEntry({
        visible: r.data.settings?.standalone_route_enabled !== false,
        has_access: true,
      }))
      .catch(() => setCompanionEntry({ visible: false, has_access: false }));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    // Load in parallel BUT swallow per-request errors (except 402) so a single
    // 404/500 on demo-status can't kill the whole fleet view with a
    // useless "Not Found" toast.
    const safeGet = async (url, fallback) => {
      try { const { data } = await axios.get(url); return data; }
      catch (e) {
        if (e.response?.status === 402) throw e; // let paywall bubble up
        console.warn('[vending] non-fatal fetch failure', url, e.response?.status);
        return fallback;
      }
    };
    try {
      const [d, m, c, l, p, ds] = await Promise.all([
        safeGet(`${API}/dashboard`,   { counts: { total: 0, deployed: 0, warehouse: 0, servicing: 0, in_transit: 0, decommissioned: 0 }, locations: 0, clients: 0 }),
        safeGet(`${API}/machines`,    { items: [] }),
        safeGet(`${API}/clients`,     { items: [] }),
        safeGet(`${API}/locations`,   { items: [] }),
        safeGet(`${API}/products`,    { items: [] }),
        safeGet(`${API}/demo-status`, null),
      ]);
      setDashboard(d);
      setMachines(m.items || []);
      setClients(c.items || []);
      setLocations(l.items || []);
      setProducts(p.items || []);
      setDemo(ds);
      setLocked(false);
    } catch (e) {
      if (e.response?.status === 402) {
        setLocked(true);
      }
    } finally { setLoading(false); }
  }, []);

  const purchaseAddon = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/modules/addon/vending`);
      toast.success('Vending Fleet activated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not activate add-on');
    }
  };

  const clearDemo = async () => {
    if (!window.confirm(`Delete ${demo?.total_demo || 0} demo records? Your live data is untouched.`)) return;
    setClearingDemo(true);
    try {
      const { data } = await axios.post(`${API}/demo-clear`);
      toast.success(`Cleared ${data.total} demo records — you're live!`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Clear failed');
    } finally { setClearingDemo(false); }
  };

  // v2.5.908 — Enterprise reseed. Wipes the current demo fleet and rebuilds
  // with 20 clients / 30 locations / 50 machines / 6 months of transactions.
  // Only for tenants whose existing demo is the OLD 8-machine seed OR who
  // want to reset the enterprise data back to a clean baseline.
  const reseedEnterprise = async () => {
    if (!window.confirm('Rebuild fleet with enterprise scale (20 clients, 30 locations, 50 machines, 6 months of transactions)?\n\nThis WIPES the current demo data and reseeds. Your live records are untouched.')) return;
    setReseedingEnterprise(true);
    try {
      const { data } = await axios.post(`${API}/enterprise-reseed`);
      const s = data.stats || {};
      toast.success(`Enterprise fleet loaded — ${s.machines || 0} machines · ${s.clients || 0} clients · ${s.transactions || 0} transactions`);
      await load();
    } catch (e) {
      if (e.response?.status === 404) {
        toast.error('Enterprise reseed endpoint not yet deployed on this backend. Redeploy the backend and try again.', { duration: 10000 });
      } else if (e.response?.status === 403) {
        toast.error('Only tenant admins can reseed the enterprise fleet.');
      } else {
        toast.error(e.response?.data?.detail || 'Reseed failed');
      }
    } finally { setReseedingEnterprise(false); }
  };

  const seedDemo = useCallback(async (opts = {}) => {
    setSeedingDemo(true);
    try {
      const { data } = await axios.post(`${API}/demo-seed`);
      if (!opts.silent) {
        if (data.skipped) toast.info('Demo fleet already loaded');
        else toast.success(`Prepopulated ${data.seeded || 'demo'} records — explore the fleet!`);
      }
      await load();
    } catch (e) {
      // v2.5.872 — Detect stale backend (endpoint 404s despite frontend v-bump).
      if (e.response?.status === 404) {
        toast.error(
          'Backend is running stale code. The demo-seed endpoint does not exist in the running process even though the frontend is up to date. Restart your backend service on the server (`sudo systemctl restart konnect-backend`) or run deploy.sh — then try again.',
          { duration: 12000 }
        );
      } else if (!opts.silent) {
        toast.error(e.response?.data?.detail || 'Could not prepopulate demo data');
      }
    } finally { setSeedingDemo(false); }
  }, [load]);

  useEffect(() => { load(); }, [load]);

  // Auto-prepopulate the vending demo fleet ONCE for tenants whose onboarding
  // ran before Vending V1 shipped. If the tenant has full access but zero
  // records + no demo data, we quietly seed so the module never looks empty.
  const [autoSeedAttempted, setAutoSeedAttempted] = useState(false);
  useEffect(() => {
    if (loading || locked || autoSeedAttempted) return;
    const totalRecords = machines.length + clients.length + locations.length + products.length;
    const hasDemo = demo?.has_demo === true;
    // Trigger when tenant clearly has nothing to show — either explicit
    // has_demo=false, or demo-status failed (demo=null) but records are empty.
    if (totalRecords === 0 && !hasDemo) {
      setAutoSeedAttempted(true);
      seedDemo({ silent: true });
    }
  }, [loading, locked, demo, machines.length, clients.length, locations.length, products.length, autoSeedAttempted, seedDemo]);

  const filteredMachines = useMemo(() => {
    return machines.filter(m => {
      if (filter.status && m.status !== filter.status) return false;
      if (filter.type && m.machine_type !== filter.type) return false;
      if (filter.search) {
        const s = filter.search.toLowerCase();
        if (!m.chassis_serial.toLowerCase().includes(s)
            && !(m.model || '').toLowerCase().includes(s)
            && !(m.vendor || '').toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [machines, filter]);

  const openCreate = (kind) => { setEditing(null); setDialog(kind); };
  const openEdit = (kind, item) => { setEditing(item); setDialog(kind); };
  const closeDialog = () => { setDialog(null); setEditing(null); };

  const clientById = (id) => clients.find(c => c.id === id);
  const locationById = (id) => locations.find(l => l.id === id);

  // ── Locked state: paid add-on not yet activated ────────────────────────
  if (locked) {
    return (
      <div className="max-w-2xl mx-auto p-4 sm:p-8" data-testid="vending-locked-splash">
        <Card className="border-2 border-amber-300">
          <CardContent className="p-6 sm:p-10 text-center">
            <Warehouse className="h-10 w-10 sm:h-14 sm:w-14 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl sm:text-2xl font-bold mb-2">Vending Fleet is a Paid Add-On</h2>
            <p className="text-sm sm:text-base text-gray-medium max-w-lg mx-auto">
              Complete Vending Machine Operator suite — assets, telemetry (Jetinno + Aurency), locations,
              planograms, route optimization, per-machine P&amp;L, and commission engine.
            </p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md mx-auto text-left">
              <div className="p-3 rounded-lg border bg-emerald-50 border-emerald-200">
                <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">Subscription</div>
                <div className="text-2xl font-bold mt-1">$299<span className="text-sm text-gray-500 font-normal">/mo</span></div>
                <div className="text-[11px] text-gray-600 mt-1">Cancel anytime</div>
              </div>
              <div className="p-3 rounded-lg border bg-sky-50 border-sky-200">
                <div className="text-[10px] uppercase tracking-wide text-sky-700 font-semibold">One-time</div>
                <div className="text-2xl font-bold mt-1">$3,299</div>
                <div className="text-[11px] text-gray-600 mt-1">Own it forever</div>
              </div>
            </div>
            <Button onClick={purchaseAddon} className="mt-6 bg-emerald-600 hover:bg-emerald-700 h-10 px-6" data-testid="vending-activate-btn">
              Activate Vending Fleet
            </Button>
            <p className="text-[11px] text-gray-400 mt-3">You&apos;ll be redirected to complete payment. Existing customers can also enable this from Settings → Modules.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-3 sm:p-6 space-y-4 sm:space-y-6" data-testid="vending-fleet-page">
      {/* Header — mobile-first */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Warehouse className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600 flex-shrink-0" />
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold" data-testid="vending-title">
              Vending Fleet<PageInfoTooltip pageKey="vending" />
            </h1>
            <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10px] sm:text-xs">V1 · Foundation</Badge>
          </div>
          <p className="text-xs sm:text-sm text-gray-medium">Manage your automated retail fleet — machines, locations, clients, products.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {companionEntry.visible && companionEntry.has_access && (
            <Button size="sm" variant="outline" onClick={() => navigate('/vending/companion')} className="h-9 text-xs sm:text-sm border-sky-300 text-sky-700 hover:bg-sky-50" data-testid="quick-action-dex-companion">
              <Radio className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">DEX Companion</span><span className="sm:hidden">Companion</span>
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => navigate('/vending/contracts')} className="h-9 text-xs sm:text-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="quick-action-contracts">
            <FileText className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Contracts</span>
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate('/vending/commissions')} className="h-9 text-xs sm:text-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="quick-action-commissions">
            <Scale className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Commissions</span>
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate('/vending/planogram-editor')} className="h-9 text-xs sm:text-sm" data-testid="quick-action-planogram">
            <Grid3x3 className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Planogram</span>
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate('/vending/routing')} className="h-9 text-xs sm:text-sm" data-testid="quick-action-routing">
            <RouteIcon className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Routing</span>
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate('/vending/reconciliation')} className="h-9 text-xs sm:text-sm" data-testid="quick-action-reconcile">
            <Scale className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Reconcile</span>
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate('/vending/dashboard-tv')} className="h-9 text-xs sm:text-sm border-slate-400 text-slate-700 hover:bg-slate-50" data-testid="quick-action-kiosk-tv">
            <Tv2 className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Kiosk TV</span>
          </Button>
          <Button size="sm" variant="outline" onClick={load} className="h-9 text-xs sm:text-sm" data-testid="vending-refresh-btn">
            <RefreshCw className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Refresh</span>
          </Button>
          {/* v2.5.897 — Replay Tour button. Resilient: dispatches an event the
              mounted VendingOnboardingTour listens to. Works even when the
              backend /tour/steps/vending/reset endpoint is 404 on the current
              deployment. */}
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              try { await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/tour/steps/vending/reset`); } catch { /* endpoint may be 404 on old deploys */ }
              try { sessionStorage.removeItem('vending_tour_dismissed'); } catch { /* storage may be blocked */ }
              window.dispatchEvent(new CustomEvent('vending-tour-replay'));
              toast.success('Vending tour restarted');
            }}
            className="h-9 text-xs sm:text-sm border-sky-300 text-sky-700 hover:bg-sky-50"
            data-testid="vending-replay-tour-btn"
          >
            <PlayCircle className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Replay Tour</span><span className="sm:hidden">Tour</span>
          </Button>
          <Button size="sm" onClick={() => openCreate('machine')} className="bg-emerald-brand hover:bg-emerald-hover h-9 text-xs sm:text-sm" data-testid="vending-add-machine">
            <Plus className="h-3.5 w-3.5 mr-1" /> <span className="hidden sm:inline">Add Machine</span><span className="sm:hidden">Machine</span>
          </Button>
        </div>
      </div>

      {/* v2.5.861 — Demo mode banner */}
      {demo?.has_demo && (
        <Card className="border-amber-200 bg-amber-50" data-testid="vending-demo-banner">
          <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-amber-900">Demo data active</div>
              <div className="text-xs sm:text-sm text-amber-800 mt-0.5">
                You&apos;re looking at {demo.total_demo} sample records so you can explore the module.
                {demo.total_live > 0 && ` Your ${demo.total_live} live records are safe.`}
                {' '}Ready to go live? Clear the demo data with one click.
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
              {/* v2.5.908 — Upgrade to Enterprise demo (20 clients, 50 machines, 6 mo txns). */}
              {machines.length < 40 && (
                <Button
                  size="sm"
                  onClick={reseedEnterprise} disabled={reseedingEnterprise || clearingDemo}
                  className="bg-sky-600 hover:bg-sky-700 text-white h-9 text-xs sm:text-sm"
                  data-testid="vending-enterprise-reseed-btn"
                >
                  {reseedingEnterprise
                    ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Loading enterprise data…</>
                    : <>Load Enterprise Demo (50 machines)</>}
                </Button>
              )}
              <Button
                size="sm" variant="outline"
                onClick={clearDemo} disabled={clearingDemo || reseedingEnterprise}
                className="border-amber-400 text-amber-800 hover:bg-amber-100 h-9 text-xs sm:text-sm"
                data-testid="vending-clear-demo-btn"
              >
                {clearingDemo ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Clearing…</> : 'Clear demo data'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* v2.5.869 — Prepopulate banner for tenants with an empty fleet.
          Always visible + prominent when user has 0 records AND no demo yet. */}
      {!loading && !locked && !demo?.has_demo && (machines.length + clients.length + locations.length + products.length === 0) && (
        <Card className="border-sky-300 bg-sky-50" data-testid="vending-prepopulate-banner">
          <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-sky-900">Fleet is empty — prepopulate with demo data?</div>
              <div className="text-xs sm:text-sm text-sky-800 mt-0.5">
                Explore Vending Fleet with 9 sample machines, 6 locations, sample clients &amp; products — all clearable in one click.
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => seedDemo()} disabled={seedingDemo}
              className="bg-sky-600 hover:bg-sky-700 text-white flex-shrink-0 h-9 text-xs sm:text-sm"
              data-testid="vending-prepopulate-btn"
            >
              {seedingDemo ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Loading…</> : <><Warehouse className="h-3.5 w-3.5 mr-1" /> Prepopulate demo fleet</>}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Dashboard stat cards */}
      {dashboard && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4" data-testid="vending-fleet-stats">
          <Card><CardContent className="p-3 sm:p-4 flex items-center justify-between">
            <div className="min-w-0"><div className="text-[10px] sm:text-xs text-gray-medium truncate">Total Machines</div>
              <div className="text-xl sm:text-2xl font-bold">{dashboard.counts.total}</div></div>
            <Box className="h-6 w-6 sm:h-8 sm:w-8 text-emerald-600/30 flex-shrink-0" />
          </CardContent></Card>
          <Card><CardContent className="p-3 sm:p-4 flex items-center justify-between">
            <div className="min-w-0"><div className="text-[10px] sm:text-xs text-gray-medium truncate">Deployed</div>
              <div className="text-xl sm:text-2xl font-bold text-emerald-600">{dashboard.counts.deployed}</div></div>
            <Activity className="h-6 w-6 sm:h-8 sm:w-8 text-emerald-600/30 flex-shrink-0" />
          </CardContent></Card>
          <Card><CardContent className="p-3 sm:p-4 flex items-center justify-between">
            <div className="min-w-0"><div className="text-[10px] sm:text-xs text-gray-medium truncate">Warehouse</div>
              <div className="text-xl sm:text-2xl font-bold text-gray-500">{dashboard.counts.warehouse}</div></div>
            <Warehouse className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400/40 flex-shrink-0" />
          </CardContent></Card>
          <Card><CardContent className="p-3 sm:p-4 flex items-center justify-between">
            <div className="min-w-0"><div className="text-[10px] sm:text-xs text-gray-medium truncate">Locations</div>
              <div className="text-xl sm:text-2xl font-bold">{dashboard.counts.locations}</div></div>
            <MapPin className="h-6 w-6 sm:h-8 sm:w-8 text-sky-500/40 flex-shrink-0" />
          </CardContent></Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-3">
        <TabsList className="w-full sm:w-auto grid grid-cols-4 sm:inline-flex bg-white border" data-testid="vending-tabs">
          <TabsTrigger value="machines" className="text-xs sm:text-sm" data-testid="tab-machines"><Box className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Machines</span></TabsTrigger>
          <TabsTrigger value="locations" className="text-xs sm:text-sm" data-testid="tab-locations"><MapPin className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Locations</span></TabsTrigger>
          <TabsTrigger value="clients" className="text-xs sm:text-sm" data-testid="tab-clients"><FileText className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Clients</span></TabsTrigger>
          <TabsTrigger value="products" className="text-xs sm:text-sm" data-testid="tab-products"><ShoppingBag className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Products</span></TabsTrigger>
        </TabsList>

        {/* ─── MACHINES TAB ────────────────────────────────────── */}
        <TabsContent value="machines" className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="relative flex-1">
              <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={filter.search} onChange={e => setFilter(f => ({ ...f, search: e.target.value }))} placeholder="Search serial, model, vendor…" className="pl-9 h-9 text-sm" data-testid="vending-search" />
            </div>
            <div className="grid grid-cols-2 sm:flex gap-2">
              <Select value={filter.status || 'all'} onValueChange={v => setFilter(f => ({ ...f, status: v === 'all' ? '' : v }))}>
                <SelectTrigger className="h-9 text-xs sm:text-sm" data-testid="vending-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="deployed">Deployed</SelectItem>
                  <SelectItem value="warehouse">Warehouse</SelectItem>
                  <SelectItem value="servicing">Servicing</SelectItem>
                  <SelectItem value="in_transit">In transit</SelectItem>
                  <SelectItem value="decommissioned">Decommissioned</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filter.type || 'all'} onValueChange={v => setFilter(f => ({ ...f, type: v === 'all' ? '' : v }))}>
                <SelectTrigger className="h-9 text-xs sm:text-sm" data-testid="vending-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading && <div className="text-sm text-gray-medium py-4 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading fleet…</div>}
          {!loading && filteredMachines.length === 0 && (
            <Card><CardContent className="p-6 text-center text-sm text-gray-medium space-y-3">
              <div>No machines yet.</div>
              <div className="flex flex-wrap gap-2 justify-center">
                <Button size="sm" variant="outline" onClick={() => seedDemo()} disabled={seedingDemo} data-testid="seed-demo-btn">
                  {seedingDemo ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Warehouse className="h-3.5 w-3.5 mr-1" />}
                  {seedingDemo ? 'Loading demo…' : 'Load demo fleet'}
                </Button>
                <Button size="sm" onClick={() => openCreate('machine')} data-testid="empty-add-machine-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add your first machine
                </Button>
              </div>
              <div className="text-[11px] text-gray-medium">Demo data is safe — you can clear it any time from the banner above.</div>
            </CardContent></Card>
          )}
          {filteredMachines.map(m => {
            const loc = locationById(m.location_id);
            return (
              <Card key={m.id} data-testid={`machine-${m.id}`} onClick={() => openEdit('machine', m)} className="cursor-pointer hover:border-emerald-300 transition-colors">
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2 flex-wrap">
                        <div className="font-semibold text-sm sm:text-base break-words min-w-0 flex-1">{m.chassis_serial}</div>
                        <Badge variant="outline" className={`text-[10px] sm:text-xs ${STATUS_STYLES[m.status] || ''}`}>{m.status}</Badge>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                        <Badge variant="outline" className="text-[10px] sm:text-xs">{TYPE_LABELS[m.machine_type] || m.machine_type}</Badge>
                        {m.model && <Badge variant="outline" className="text-[10px] sm:text-xs text-gray-600">{m.vendor} {m.model}</Badge>}
                        <Badge variant="outline" className="text-[10px] sm:text-xs">telemetry: {m.telemetry_provider}</Badge>
                        <Badge variant="outline" className="text-[10px] sm:text-xs">{m.currency}</Badge>
                      </div>
                      {loc && (
                        <div className="mt-2 text-xs sm:text-sm text-gray-medium flex items-start gap-1">
                          <MapPin className="h-3.5 w-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                          <span className="min-w-0">{loc.name}{loc.city ? `, ${loc.city}` : ''}</span>
                        </div>
                      )}
                      <div className="text-[11px] sm:text-xs text-gray-400 mt-2">
                        Vends: {m.total_vends || 0} · Revenue: {m.currency} {(m.total_revenue || 0).toFixed(2)} · CapEx: {m.currency} {(m.capex || 0).toFixed(0)}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0 self-center hidden sm:block" />
                  </div>
                  <div className="mt-2 flex justify-end">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-[11px] text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                      onClick={async (e) => {
                        e.stopPropagation();
                        // v2.5.907 — the QR endpoint requires bearer auth. Opening
                        // it in a new tab loses the Authorization header and
                        // returns 401 → "Not Found" in production. Fetch via
                        // axios (which injects auth), convert to blob, then
                        // open a printable HTML window with the PNG embedded.
                        try {
                          toast.loading('Generating QR label…', { id: `qr-${m.id}` });
                          const res = await axios.get(
                            `${process.env.REACT_APP_BACKEND_URL}/api/vending/machines/${m.id}/qr?size=10`,
                            { responseType: 'blob' },
                          );
                          const blob = new Blob([res.data], { type: 'image/png' });
                          const objUrl = URL.createObjectURL(blob);
                          const chassis = m.chassis_serial || m.id;
                          const win = window.open('', '_blank');
                          if (!win) {
                            toast.error('Pop-up blocked. Allow pop-ups for this site.', { id: `qr-${m.id}` });
                            return;
                          }
                          win.document.write(`
                            <html><head><title>QR — ${chassis}</title>
                            <style>
                              body { font-family: system-ui, sans-serif; text-align:center; padding: 40px; background: #f8fafc; }
                              .card { display:inline-block; background:#fff; padding:32px 48px; border-radius:16px; box-shadow: 0 8px 24px rgba(0,0,0,0.08); }
                              h1 { font-size: 18px; margin: 0 0 4px; color:#0f172a; letter-spacing: 0.5px; }
                              .subtitle { font-size: 12px; color:#64748b; margin-bottom: 20px; }
                              img { width: 240px; height: 240px; }
                              .chassis { font-family: 'Courier New', monospace; font-size: 14px; color:#0f172a; margin-top: 16px; padding: 6px 12px; background: #f1f5f9; border-radius: 6px; display: inline-block; }
                              button { margin-top: 20px; padding: 8px 18px; background:#0284c7; color:white; border: none; border-radius: 6px; cursor: pointer; font-size: 13px; }
                              button:hover { background:#0369a1; }
                              @media print { body { background: white; padding: 20px; } .card { box-shadow: none; padding: 12px; } button { display: none; } }
                            </style></head><body>
                            <div class="card">
                              <h1>${m.vendor || ''} ${m.model || ''}</h1>
                              <div class="subtitle">Scan to open DEX Companion</div>
                              <img src="${objUrl}" alt="QR Code" />
                              <div class="chassis">${chassis}</div>
                              <br /><button onclick="window.print()">Print</button>
                            </div>
                            </body></html>
                          `);
                          win.document.close();
                          toast.success('QR ready. Preview open in new tab.', { id: `qr-${m.id}` });
                        } catch (err) {
                          const msg = err.response?.status === 404 ? 'Machine not found on this tenant.' : (err.response?.data?.detail || err.message || 'Could not fetch QR');
                          toast.error(msg, { id: `qr-${m.id}` });
                        }
                      }}
                      data-testid={`machine-qr-${m.id}`}
                    >
                      <QrCode className="h-3.5 w-3.5 mr-1" /> Print QR
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* ─── LOCATIONS TAB ───────────────────────────────────── */}
        <TabsContent value="locations" className="space-y-3">
          <div className="flex justify-end">
            <Button size="sm" onClick={() => openCreate('location')} className="bg-emerald-brand hover:bg-emerald-hover h-9 text-xs sm:text-sm" data-testid="vending-add-location">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Location
            </Button>
          </div>
          {locations.length === 0 && (
            <Card><CardContent className="p-6 text-center text-sm text-gray-medium">No locations yet.</CardContent></Card>
          )}
          {locations.map(l => (
            <Card key={l.id} data-testid={`location-${l.id}`} onClick={() => openEdit('location', l)} className="cursor-pointer hover:border-emerald-300 transition-colors">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm sm:text-base break-words">{l.name}</div>
                    <div className="flex items-center gap-1.5 flex-wrap mt-1">
                      <Badge variant="outline" className="text-[10px] sm:text-xs">{l.site_type}</Badge>
                      <Badge variant="outline" className="text-[10px] sm:text-xs">{clientById(l.client_id)?.name || 'Unknown client'}</Badge>
                    </div>
                    <div className="text-xs sm:text-sm text-gray-medium mt-2 flex items-start gap-1">
                      <MapPin className="h-3.5 w-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <span>{[l.address_line1, l.city, l.region, l.country].filter(Boolean).join(', ') || 'No address'}</span>
                    </div>
                    {l.primary_contact?.full_name && <div className="text-[11px] sm:text-xs text-gray-400 mt-1">Contact: {l.primary_contact.full_name}{l.primary_contact.mobile ? ` · ${l.primary_contact.mobile}` : ''}</div>}
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0 self-center hidden sm:block" />
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* ─── CLIENTS TAB ────────────────────────────────────── */}
        <TabsContent value="clients" className="space-y-3">
          <div className="flex justify-end">
            <Button size="sm" onClick={() => openCreate('client')} className="bg-emerald-brand hover:bg-emerald-hover h-9 text-xs sm:text-sm" data-testid="vending-add-client">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Client
            </Button>
          </div>
          {clients.length === 0 && (
            <Card><CardContent className="p-6 text-center text-sm text-gray-medium">No clients yet.</CardContent></Card>
          )}
          {clients.map(c => {
            const locCount = locations.filter(l => l.client_id === c.id).length;
            return (
              <Card key={c.id} data-testid={`client-${c.id}`} onClick={() => openEdit('client', c)} className="cursor-pointer hover:border-emerald-300 transition-colors">
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm sm:text-base break-words">{c.name}</div>
                      <div className="flex items-center gap-1.5 flex-wrap mt-1">
                        <Badge variant="outline" className="text-[10px] sm:text-xs">{c.billing_model.replace('_', ' ')}</Badge>
                        <Badge variant="outline" className="text-[10px] sm:text-xs">{locCount} location{locCount === 1 ? '' : 's'}</Badge>
                      </div>
                      {c.billing_email && <div className="text-xs sm:text-sm text-gray-medium mt-2">{c.billing_email}</div>}
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0 self-center hidden sm:block" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* ─── PRODUCTS TAB ───────────────────────────────────── */}
        <TabsContent value="products" className="space-y-3">
          <div className="flex justify-end">
            <Button size="sm" onClick={() => openCreate('product')} className="bg-emerald-brand hover:bg-emerald-hover h-9 text-xs sm:text-sm" data-testid="vending-add-product">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Product
            </Button>
          </div>
          {products.length === 0 && (
            <Card><CardContent className="p-6 text-center text-sm text-gray-medium">No products in catalog yet.</CardContent></Card>
          )}
          {products.map(p => {
            const margin = p.retail_price - p.cost;
            const marginPct = p.retail_price ? (margin / p.retail_price * 100).toFixed(0) : '0';
            return (
              <Card key={p.id} data-testid={`product-${p.id}`} onClick={() => openEdit('product', p)} className="cursor-pointer hover:border-emerald-300 transition-colors">
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2 flex-wrap">
                        <div className="font-semibold text-sm sm:text-base break-words min-w-0 flex-1">{p.name}</div>
                        <Badge variant="outline" className="text-[10px] sm:text-xs font-mono">{p.sku}</Badge>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap mt-1">
                        <Badge variant="outline" className="text-[10px] sm:text-xs">{p.category}</Badge>
                        {p.is_recipe_based && <Badge variant="outline" className="text-[10px] sm:text-xs bg-violet-50 border-violet-200 text-violet-700">recipe-based</Badge>}
                        {p.track_expiration && <Badge variant="outline" className="text-[10px] sm:text-xs bg-amber-50 border-amber-200 text-amber-700">expires</Badge>}
                      </div>
                      <div className="text-[11px] sm:text-xs text-gray-medium mt-2">
                        Cost: {p.currency} {p.cost.toFixed(2)} · Retail: {p.currency} {p.retail_price.toFixed(2)} · Margin: <span className="font-semibold text-emerald-700">{marginPct}%</span>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0 self-center hidden sm:block" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>
      </Tabs>

      {/* ─── DIALOGS ─────────────────────────────────────────── */}
      {dialog === 'client' ? (
        <RichVendingClientDialog
          client={editing}
          onClose={closeDialog}
          onSaved={() => { closeDialog(); load(); }}
        />
      ) : (
        <VendingDialog dialog={dialog} editing={editing} clients={clients} locations={locations} onClose={closeDialog} onSaved={() => { closeDialog(); load(); }} />
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════ */
/*  Shared create/edit dialog — one component handles all four entity types   */
/* ═══════════════════════════════════════════════════════════════════════════ */
function VendingDialog({ dialog, editing, clients, locations, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!dialog) return;
    setForm(editing ? { ...editing } : defaultForm(dialog));
  }, [dialog, editing]);

  const save = async () => {
    setBusy(true);
    try {
      // v2.5.907 — Catalog-driven create: if a catalog_id was picked, route to
      // the enterprise "from-catalog" endpoint which auto-planograms every
      // slot from the model's default_layout. Any other case (edit, no
      // catalog) falls through to the standard CRUD path unchanged.
      if (dialog === 'machine' && !editing && form.catalog_id) {
        if (!form.chassis_serial || !form.chassis_serial.trim()) {
          toast.error('Chassis serial is required');
          setBusy(false);
          return;
        }
        const res = await axios.post(`${API}/enterprise/machines/from-catalog`, {
          catalog_id: form.catalog_id,
          chassis_serial: form.chassis_serial.trim(),
          location_id: form.location_id || '',
          contract_id: form.contract_id || '',
          telemetry_device_id: form.telemetry_device_id || '',
          telemetry_provider: form.telemetry_provider || null,
          currency: form.currency || '',
          capex: form.capex ?? 0,
          notes: form.notes || '',
        });
        toast.success(`Machine created — ${res.data?.slots_created || 0} slots auto-planogrammed`);
        onSaved();
        return;
      }
      const endpoint = { machine: 'machines', client: 'clients', location: 'locations', product: 'products' }[dialog];
      const url = `${API}/${endpoint}${editing ? '/' + editing.id : ''}`;
      const method = editing ? 'patch' : 'post';
      // Strip server-managed and catalog-only fields before sending
      const body = { ...form };
      ['id', 'tenant_id', 'created_at', 'updated_at', 'created_by', 'total_vends', 'total_revenue', 'total_downtime_hours', 'catalog_id'].forEach(k => delete body[k]);
      await axios[method](url, body);
      toast.success(editing ? 'Updated' : 'Created');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!editing) return;
    if (!window.confirm(`Delete this ${dialog}?`)) return;
    setBusy(true);
    try {
      const endpoint = { machine: 'machines', client: 'clients', location: 'locations', product: 'products' }[dialog];
      await axios.delete(`${API}/${endpoint}/${editing.id}`);
      toast.success('Deleted');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally { setBusy(false); }
  };

  if (!dialog) return null;

  return (
    <Dialog open={!!dialog} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid={`vending-${dialog}-dialog`}>
        <DialogHeader><DialogTitle className="text-base sm:text-lg">
          {editing ? 'Edit' : 'Add'} {dialog}
        </DialogTitle></DialogHeader>

        {/* ─ MACHINE FORM ─ */}
        {dialog === 'machine' && (
          <div className="space-y-3 py-2">
            {/* v2.5.907 — Catalog picker: pre-populate every field from the
                Machine Model Catalog (Jetinno / Necta / FAS / Bianchi / TCN /
                Crane / AMS / Sielaff / Aurency / etc). Auto-creates the slots
                from the model's default_layout when you save. */}
            <CatalogPicker
              onPick={(cat) => {
                if (!cat) {
                  setForm(f => ({ ...f, catalog_id: '', vendor: '', model: '', machine_type: 'snack', telemetry_provider: 'manual' }));
                  return;
                }
                setForm(f => ({
                  ...f,
                  catalog_id: cat.id,
                  vendor: cat.vendor,
                  model: cat.model,
                  machine_type: cat.machine_type,
                  telemetry_provider: (cat.telemetry_support && cat.telemetry_support[0]) || f.telemetry_provider || 'manual',
                }));
                toast.success(`${cat.vendor} ${cat.model} loaded — ${cat.slot_count} slots will be auto-planogrammed on save`);
              }}
              selectedCatalogId={form.catalog_id}
            />
            <FormField label="Chassis serial *"><Input value={form.chassis_serial || ''} onChange={e => setForm(f => ({ ...f, chassis_serial: e.target.value }))} data-testid="machine-serial-input" /></FormField>
            <div className="grid grid-cols-2 gap-2">
              <FormField label="Vendor"><Input value={form.vendor || ''} onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))} placeholder="Jetinno" /></FormField>
              <FormField label="Model"><Input value={form.model || ''} onChange={e => setForm(f => ({ ...f, model: e.target.value }))} /></FormField>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <FormField label="Machine type">
                <Select value={form.machine_type || 'snack'} onValueChange={v => setForm(f => ({ ...f, machine_type: v }))}>
                  <SelectTrigger data-testid="machine-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </FormField>
              <FormField label="Status">
                <Select value={form.status || 'warehouse'} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="warehouse">Warehouse</SelectItem>
                    <SelectItem value="deployed">Deployed</SelectItem>
                    <SelectItem value="servicing">Servicing</SelectItem>
                    <SelectItem value="in_transit">In transit</SelectItem>
                    <SelectItem value="decommissioned">Decommissioned</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <FormField label="Telemetry provider">
                <Select value={form.telemetry_provider || 'manual'} onValueChange={v => setForm(f => ({ ...f, telemetry_provider: v }))}>
                  <SelectTrigger data-testid="machine-telemetry-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="jetinno">Jetinno</SelectItem>
                    <SelectItem value="aurency">Aurency</SelectItem>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Telemetry device ID"><Input value={form.telemetry_device_id || ''} onChange={e => setForm(f => ({ ...f, telemetry_device_id: e.target.value }))} /></FormField>
            </div>
            <FormField label="Location">
              <Select value={form.location_id || 'none'} onValueChange={v => setForm(f => ({ ...f, location_id: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Warehouse (unassigned)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Warehouse (unassigned)</SelectItem>
                  {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            <div className="grid grid-cols-2 gap-2">
              <FormField label="Currency (blank = inherit)"><Input value={form.currency || ''} onChange={e => setForm(f => ({ ...f, currency: e.target.value.toUpperCase() }))} placeholder="PHP / USD" /></FormField>
              <FormField label="CapEx"><Input type="number" step="0.01" value={form.capex ?? 0} onChange={e => setForm(f => ({ ...f, capex: parseFloat(e.target.value) || 0 }))} /></FormField>
            </div>
            <FormField label="Notes"><Textarea value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} /></FormField>
          </div>
        )}

        {/* ─ CLIENT FORM ─ */}
        {dialog === 'client' && (
          <div className="space-y-3 py-2">
            <FormField label="Client name *"><Input value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="client-name-input" /></FormField>
            <FormField label="Billing email"><Input type="email" value={form.billing_email || ''} onChange={e => setForm(f => ({ ...f, billing_email: e.target.value }))} /></FormField>
            <FormField label="Billing model">
              <Select value={form.billing_model || 'paying_vend'} onValueChange={v => setForm(f => ({ ...f, billing_model: v }))}>
                <SelectTrigger data-testid="client-billing-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="paying_vend">Paying vend (end-user pays)</SelectItem>
                  <SelectItem value="freevend">Freevend (client subsidizes)</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Notes"><Textarea value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} /></FormField>
          </div>
        )}

        {/* ─ LOCATION FORM ─ */}
        {dialog === 'location' && (
          <div className="space-y-3 py-2">
            <FormField label="Client *">
              <Select value={form.client_id || ''} onValueChange={v => setForm(f => ({ ...f, client_id: v }))}>
                <SelectTrigger data-testid="location-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </FormField>
            <FormField label="Location name *"><Input value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Main breakroom - 3rd floor" data-testid="location-name-input" /></FormField>
            <FormField label="Site type">
              <Select value={form.site_type || 'office'} onValueChange={v => setForm(f => ({ ...f, site_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="office">Office</SelectItem>
                  <SelectItem value="hospital">Hospital</SelectItem>
                  <SelectItem value="factory">Factory</SelectItem>
                  <SelectItem value="school">School</SelectItem>
                  <SelectItem value="gym">Gym</SelectItem>
                  <SelectItem value="mall">Mall / retail</SelectItem>
                  <SelectItem value="transit">Transit hub</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Address line 1"><Input value={form.address_line1 || ''} onChange={e => setForm(f => ({ ...f, address_line1: e.target.value }))} /></FormField>
            <div className="grid grid-cols-2 gap-2">
              <FormField label="City"><Input value={form.city || ''} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} /></FormField>
              <FormField label="Country (ISO)"><Input maxLength={2} value={form.country || ''} onChange={e => setForm(f => ({ ...f, country: e.target.value.toUpperCase() }))} placeholder="PH" /></FormField>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <FormField label="Latitude"><Input type="number" step="any" value={form.lat ?? ''} onChange={e => setForm(f => ({ ...f, lat: e.target.value === '' ? null : parseFloat(e.target.value) }))} /></FormField>
              <FormField label="Longitude"><Input type="number" step="any" value={form.lng ?? ''} onChange={e => setForm(f => ({ ...f, lng: e.target.value === '' ? null : parseFloat(e.target.value) }))} /></FormField>
            </div>
            <FormField label="Placement note"><Input value={form.placement_note || ''} onChange={e => setForm(f => ({ ...f, placement_note: e.target.value }))} placeholder="3rd floor breakroom past security" /></FormField>
            <div className="p-3 bg-gray-50 rounded border">
              <div className="text-xs font-semibold text-gray-700 mb-2">Primary contact</div>
              <div className="space-y-2">
                <Input placeholder="Full name" value={form.primary_contact?.full_name || ''} onChange={e => setForm(f => ({ ...f, primary_contact: { ...(f.primary_contact || {}), full_name: e.target.value } }))} />
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Mobile" value={form.primary_contact?.mobile || ''} onChange={e => setForm(f => ({ ...f, primary_contact: { ...(f.primary_contact || {}), mobile: e.target.value } }))} />
                  <Input placeholder="Email" type="email" value={form.primary_contact?.email || ''} onChange={e => setForm(f => ({ ...f, primary_contact: { ...(f.primary_contact || {}), email: e.target.value } }))} />
                </div>
                <Input placeholder="Birthday (YYYY-MM-DD)" value={form.primary_contact?.birthday || ''} onChange={e => setForm(f => ({ ...f, primary_contact: { ...(f.primary_contact || {}), birthday: e.target.value } }))} />
              </div>
            </div>
          </div>
        )}

        {/* ─ PRODUCT FORM ─ */}
        {dialog === 'product' && (
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-2">
              <FormField label="SKU *"><Input value={form.sku || ''} onChange={e => setForm(f => ({ ...f, sku: e.target.value.toUpperCase() }))} data-testid="product-sku-input" /></FormField>
              <FormField label="Category"><Input value={form.category || ''} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} placeholder="snack / drink / hot" /></FormField>
            </div>
            <FormField label="Product name *"><Input value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="product-name-input" /></FormField>
            <div className="grid grid-cols-3 gap-2">
              <FormField label="Cost"><Input type="number" step="0.01" value={form.cost ?? 0} onChange={e => setForm(f => ({ ...f, cost: parseFloat(e.target.value) || 0 }))} /></FormField>
              <FormField label="Retail"><Input type="number" step="0.01" value={form.retail_price ?? 0} onChange={e => setForm(f => ({ ...f, retail_price: parseFloat(e.target.value) || 0 }))} /></FormField>
              <FormField label="Currency"><Input maxLength={3} value={form.currency || ''} onChange={e => setForm(f => ({ ...f, currency: e.target.value.toUpperCase() }))} placeholder="PHP" /></FormField>
            </div>
            <FormField label="Barcode"><Input value={form.barcode || ''} onChange={e => setForm(f => ({ ...f, barcode: e.target.value }))} /></FormField>
            <div className="flex items-center gap-4 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!form.is_recipe_based} onChange={e => setForm(f => ({ ...f, is_recipe_based: e.target.checked }))} data-testid="product-recipe-checkbox" />
                Recipe-based (BOM)
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!form.track_expiration} onChange={e => setForm(f => ({ ...f, track_expiration: e.target.checked }))} />
                Track expiration (FIFO)
              </label>
            </div>
          </div>
        )}

        <div className="flex justify-between items-center gap-2 pt-3 border-t">
          {editing ? (
            <Button variant="ghost" onClick={remove} disabled={busy} className="text-red-600 hover:text-red-700 h-9 text-xs sm:text-sm" data-testid="vending-delete-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
            </Button>
          ) : <div />}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy} className="h-9 text-xs sm:text-sm" data-testid="vending-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 h-9 text-xs sm:text-sm" data-testid="vending-save-btn">
              {busy ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Saving…</> : (editing ? 'Save' : 'Create')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function FormField({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] sm:text-xs text-gray-700 font-medium uppercase tracking-wide">{label}</Label>
      {children}
    </div>
  );
}

function defaultForm(dialog) {
  if (dialog === 'machine') return { chassis_serial: '', vendor: '', model: '', machine_type: 'snack', status: 'warehouse', telemetry_provider: 'manual', currency: '', capex: 0, notes: '' };
  if (dialog === 'client') return { name: '', billing_email: '', billing_model: 'paying_vend', notes: '' };
  if (dialog === 'location') return { client_id: '', name: '', site_type: 'office', address_line1: '', city: '', country: '', primary_contact: { full_name: '', mobile: '', email: '', birthday: '' } };
  if (dialog === 'product') return { sku: '', name: '', category: 'snack', cost: 0, retail_price: 0, currency: '', is_recipe_based: false, track_expiration: false };
  return {};
}
